import { useEffect, useMemo, useState } from 'react';
import { Api } from '../../services/api';
import AddAddressModal from '../checkout/AddAddressModal';
import { useLanguage } from '../../context/LanguageContext';

const toNumber = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const toCurrency = (value) =>
  `$${Number(value ?? 0).toFixed(2)}`;

const normalizeAddressList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.addresses)) return payload.addresses;
  return [];
};

const getStoredCustomerId = () => {
  if (typeof window === 'undefined') return null;
  try {
    const meta = JSON.parse(window.localStorage.getItem('myUser'));
    return meta?.customer_id || null;
  } catch {
    return null;
  }
};

const CheckoutInquiryPage = () => {
  const [items, setItems] = useState([]);
  const [checkoutSource, setCheckoutSource] = useState('inquiry');
  const [notes, setNotes] = useState('');
  const [countryMode, setCountryMode] = useState('LB');
  const [customCountry, setCustomCountry] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [addressError, setAddressError] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [placeOrderError, setPlaceOrderError] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [pendingSelectAddressId, setPendingSelectAddressId] = useState(null);
  const { t } = useLanguage();

  const isCartFlow = checkoutSource === 'cart';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const parseArray = (key) => {
      try {
        const raw = window.sessionStorage.getItem(key);
        const data = raw ? JSON.parse(raw) : [];
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    };

    const inquiryItems = parseArray('pendingInquiryItems');
    if (inquiryItems.length) {
      setCheckoutSource('inquiry');
      setItems(inquiryItems);
      return;
    }

    const cartItems = parseArray('pendingCartItems');
    if (cartItems.length) {
      setCheckoutSource('cart');
      setItems(cartItems);
      return;
    }

    window.location.assign('/cart');
  }, []);

  useEffect(() => {
    setCustomerId(getStoredCustomerId());
  }, []);

  useEffect(() => {
    if (!customerId) {
      setLoadingAddresses(false);
      setAddressError(t('checkout.address_error_missing_customer'));
      return;
    }

    let cancelled = false;
    const loadAddresses = async () => {
      setLoadingAddresses(true);
      setAddressError(null);
      try {
        const response = await Api.fetchCustomerAddresses(customerId);
        if (cancelled) return;
        const list = normalizeAddressList(response).map((entry) => ({
          ...entry,
          id: entry.addressId || entry.id || entry._id,
        }));
        setAddresses(list);
        if (list.length) {
          if (!selectedAddressId) {
            const defaultId = list[0]?.id;
            if (defaultId != null) {
              setSelectedAddressId(String(defaultId));
            }
          } else if (
            pendingSelectAddressId &&
            list.some(
              (entry) => String(entry.id) === String(pendingSelectAddressId)
            )
          ) {
            setSelectedAddressId(String(pendingSelectAddressId));
            setPendingSelectAddressId(null);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setAddressError(
            error?.message || t('checkout.address_error_generic')
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingAddresses(false);
        }
      }
    };
    loadAddresses();

    return () => {
      cancelled = true;
    };
  }, [customerId, refreshKey, pendingSelectAddressId, selectedAddressId, t]);

  useEffect(() => {
    if (!pendingSelectAddressId) return;
    const exists = addresses.some(
      (entry) => String(entry.id) === String(pendingSelectAddressId)
    );
    if (exists) {
      setSelectedAddressId(String(pendingSelectAddressId));
      setPendingSelectAddressId(null);
    }
  }, [addresses, pendingSelectAddressId]);

  const selectedAddress = useMemo(
    () =>
      addresses.find(
        (entry) => String(entry.id) === String(selectedAddressId || '')
      ) || null,
    [addresses, selectedAddressId]
  );

  const getLineUnit = (item) =>
    toNumber(
      item?.price ??
        item?.priceUnit ??
        item?.sellingPrice ??
        item?.originalPrice
    );

  const getLineQuantity = (item) =>
    isCartFlow ? Math.max(1, toNumber(item?.quantity) || 1) : 1;

  const itemsSubtotal = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + getLineUnit(item) * getLineQuantity(item),
        0
      ),
    [items, isCartFlow]
  );

  const hasTbdByItems = useMemo(
    () =>
      isCartFlow
        ? false
        : items.some((item) => {
            const typePrice = toNumber(item?.productType?.price);
            const itemPrice = toNumber(item?.price);
            return !item?.productType || typePrice <= 0 || itemPrice >= 200;
          }),
    [items, isCartFlow]
  );

  const shippingSum = useMemo(() => {
    if (isCartFlow) {
      return items.reduce(
        (sum, item) =>
          sum + toNumber(item.shipping) * getLineQuantity(item),
        0
      );
    }
    return items.reduce(
      (sum, item) => sum + toNumber(item?.productType?.price),
      0
    );
  }, [items, isCartFlow]);

  const isLebanon = countryMode === 'LB';
  const shippingValue = isLebanon
    ? isCartFlow
      ? shippingSum
      : hasTbdByItems
      ? null
      : shippingSum
    : null;
  const totalValue =
    shippingValue == null ? null : shippingValue + itemsSubtotal;

  const shippingText =
    shippingValue == null
      ? t('checkoutInquiry.shipping_tbd')
      : toCurrency(shippingValue);
  const totalPaymentText =
    totalValue == null
      ? t('checkoutInquiry.shipping_tbd')
      : toCurrency(totalValue);

  const destinationLabel = isLebanon
    ? t('checkoutInquiry.lebanon')
    : customCountry || t('checkoutInquiry.destination_label');

  const disableOrderButton =
    placingOrder ||
    !items.length ||
    !selectedAddress ||
    (!isLebanon && !customCountry.trim());

  const handleRefreshAddresses = () => {
    setRefreshKey((key) => key + 1);
  };

  const handleAddressCreated = (createdAddress) => {
    if (createdAddress) {
      const newId =
        createdAddress?.addressId || createdAddress?.id || createdAddress?._id;
      if (newId != null) {
        setPendingSelectAddressId(String(newId));
      }
    }
    setAddressModalOpen(false);
    handleRefreshAddresses();
  };

  const handlePlaceOrder = async () => {
    if (disableOrderButton) return;
    setPlaceOrderError(null);
    setPlacingOrder(true);

    try {
      if (!customerId) {
        throw new Error(t('checkoutInquiry.place_order_error_missing_customer'));
      }

      const payloadItems = items.map((item) => {
        if (isCartFlow) {
          return {
            name: item.name || item.text || 'Item',
            price: getLineUnit(item),
            service: 0,
            shipping: toNumber(item.shipping),
            image: item.image,
            typeVariant: item.variantId
              ? { type_variant_id: item.variantId }
              : null,
            quantity: getLineQuantity(item),
          };
        }
        return {
          name: item.text?.trim() || 'Item',
          price: toNumber(item.price),
          service: toNumber(item?.productType?.service),
          shipping: toNumber(
            item?.productType?.shipping ?? item?.productType?.price
          ),
          image: item.image,
          typeVariant: item?.productType?.id
            ? { type_variant_id: item.productType.id }
            : null,
        };
      });

      const payload = {
        customer: { customerId },
        items: payloadItems,
        pickup: false,
        deliveryCharge: shippingValue ?? 0,
        receiverNumber: null,
        totalAmount:
          totalValue == null ? null : Number(totalValue ?? 0).toFixed(2),
        promoCode: null,
        notes: notes || null,
        address: selectedAddress?.addressId || selectedAddress?.id
          ? { addressId: selectedAddress.addressId || selectedAddress.id }
          : null,
        country: isLebanon ? 'Lebanon' : customCountry.trim(),
      };

      const response = await Api.createOrder(payload);
      const success =
        response?.status === true ||
        response?.success === true ||
        response?.data?.status === true;

      if (!success) {
        const message =
          response?.message ||
          response?.data?.message ||
          t('checkoutInquiry.place_order_error_generic');
        throw new Error(message);
      }

      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem('pendingInquiryItems');
        window.sessionStorage.removeItem('pendingCartItems');
        window.location.assign('/inquiry-success');
      }
    } catch (error) {
      setPlaceOrderError(
        error?.message || t('checkoutInquiry.place_order_error_generic')
      );
    } finally {
      setPlacingOrder(false);
    }
  };

  return (
    <div className="checkout">
      <section className="checkout__hero">
        <div>
          <h1>
            {isCartFlow
              ? t('checkoutInquiry.title_cart')
              : t('checkoutInquiry.title_inquiry')}
          </h1>
          <p>
            {isCartFlow
              ? t('checkoutInquiry.description_cart')
              : t('checkoutInquiry.description_inquiry')}
          </p>
        </div>
      </section>

      <section className="checkout-card">
        <header className="checkout-card__header">
          <div>
            <p className="eyebrow">{t('checkoutInquiry.destination_label')}</p>
            <h3>{destinationLabel}</h3>
          </div>
        </header>
        <div className="checkout-destination">
          <button
            type="button"
            className={`checkout-pill ${isLebanon ? 'is-active' : ''}`}
            onClick={() => setCountryMode('LB')}
          >
            {t('checkoutInquiry.lebanon')}
          </button>
          <button
            type="button"
            className={`checkout-pill ${!isLebanon ? 'is-active' : ''}`}
            onClick={() => setCountryMode('OTHER')}
          >
            {t('checkoutInquiry.other_countries')}
          </button>
        </div>
        {!isLebanon && (
          <>
            <p className="checkout-hint">
              {t('checkoutInquiry.shipping_hint')}
            </p>
            <input
              type="text"
              className="checkout-input"
              placeholder={t('checkoutInquiry.destination_placeholder')}
              value={customCountry}
              onChange={(event) => setCustomCountry(event.target.value)}
            />
          </>
        )}
      </section>

      {items.length > 0 && (
        <section className="checkout-card">
          <header className="checkout-card__header">
            <div>
              <h3>
                {t('checkoutInquiry.items_heading')} - ({items.length}{' '}
                {t(
                  items.length === 1
                    ? 'checkoutInquiry.items_count'
                    : 'checkoutInquiry.items_count_plural'
                )}
                )
              </h3>
              
            </div>
            <p>{toCurrency(itemsSubtotal)}</p>
          </header>

          <div className="checkout-items">
            {items.map((item) => {
              const key =
                item.key || item.id || `${item.productId || ''}:${item.variantId || ''}`;
              const unitPrice = getLineUnit(item);
              const quantity = getLineQuantity(item);
              const lineName = item.text || item.name || t('checkoutInquiry.item_fallback');
              const shippingLabel = isCartFlow
                ? item.shipping
                  ? toCurrency(item.shipping)
                  : t('checkoutInquiry.shipping_included')
                : item?.productType?.price
                ? toCurrency(toNumber(item.productType.price))
                : t('checkoutInquiry.shipping_tbd');
              return (
                <article className="checkout-item" key={key}>
                  <div className="checkout-item__media">
                    {item.image ? (
                      <img src={item.image} alt={lineName} />
                    ) : (
                      <span>{t('checkoutInquiry.no_image')}</span>
                    )}
                  </div>
                  <div className="checkout-item__body">
                    <div className="checkout-item__row">
                      <p className="checkout-item__title">{lineName}</p>
                      <p className="checkout-item__price">
                        {toCurrency(unitPrice)}
                        {isCartFlow && quantity > 1
                          ? ` x ${quantity}`
                          : ''}
                      </p>
                    </div>
                    <p className="checkout-item__meta">
                      {t('checkoutInquiry.shipping_prefix')} {shippingLabel}
                    </p>
                    {item?.description && (
                      <p className="checkout-item__description">
                        {item.description}
                      </p>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      <section className="checkout-card">
        <header className="checkout-section-header">
          <div>
            <h3>{t('checkoutInquiry.select_address')}</h3>
          </div>
          <div className="checkout-section-actions">
           
            <button
              type="button"
              className="link-button"
              onClick={() => setAddressModalOpen(true)}
              disabled={!customerId}
            >
              {t('checkoutInquiry.address_add')}
            </button>
          </div>
        </header>

        {addressError && (
          <p className="checkout-error">{addressError}</p>
        )}

        {!loadingAddresses && !addresses.length && !addressError && (
          <p className="checkout-hint">
            {t('checkoutInquiry.address_hint')}
          </p>
        )}

        {addresses.length > 0 && (
          <label className="checkout-field">
            <span>{t('checkoutInquiry.address_label')}</span>
            <select
              value={selectedAddressId}
              onChange={(event) => setSelectedAddressId(event.target.value)}
            >
              {addresses.map((address) => (
                <option key={address.id} value={address.id}>
                  {address.name || address.label || `Address ${address.id}`}
                </option>
              ))}
            </select>
          </label>
        )}

        {selectedAddress && (
          <div className="checkout-address-preview">
            <p>
              {selectedAddress.street || selectedAddress.building || selectedAddress.apartment
                ? [selectedAddress.street, selectedAddress.building, selectedAddress.apartment]
                    .filter(Boolean)
                    .join(', ')
                : t('checkoutInquiry.address_no_details')}
            </p>
          </div>
        )}
      </section>

      <section className="checkout-card">
        <label className="checkout-field">
          <span>{t('checkoutInquiry.delivery_label')}</span>
          <textarea
            placeholder={t('checkoutInquiry.delivery_placeholder')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>
      </section>

      <section className="checkout-card">
        <div className="checkout-summary-row">
          <p>{t('checkoutInquiry.summary_items')}</p>
          <p>{items.length}</p>
        </div>
        <div className="checkout-summary-row">
          <p>{t('checkoutInquiry.summary_subtotal')}</p>
          <p>{toCurrency(itemsSubtotal)}</p>
        </div>
        <div className="checkout-summary-row">
          <p>{t('checkoutInquiry.summary_shipping')}</p>
          <p>{shippingText}</p>
        </div>
        <div className="checkout-summary-total">
          <p>{t('checkoutInquiry.summary_total')}</p>
          <p>{totalPaymentText}</p>
        </div>
      </section>

      {placeOrderError && (
        <p className="checkout-error">{placeOrderError}</p>
      )}

      <div className="checkout__actions">
        <button
          type="button"
          className="button"
          onClick={handlePlaceOrder}
          disabled={disableOrderButton}
        >
          {placingOrder ? t('checkoutInquiry.submitting') : t('checkoutInquiry.submit')}
        </button>
        
      </div>

      <AddAddressModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        customerId={customerId}
        onCreated={handleAddressCreated}
      />
    </div>
  );
};

export default CheckoutInquiryPage;
